// src/pages/my/MyWorkPage.tsx
// Personal work hub — replaces MyTasksPage.

import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useTask, useI18n } from '@react/hooks';
import {
  Badge,
  Button,
  Card,
  CardContent,
  ErrorAlert,
  Input,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@react/components';

import type { Task } from '@core/types/task';
import type { MyWorkTab, UrgencyGroup, FocusSegment } from '@/features/my-work/types';

import {
  computeFocusStats,
  groupTasksByUrgency,
  getMyWorkTabFilter,
  filterMyWorkTasks,
  filterTasksByFocusSegment,
  URGENCY_GROUPS,
  MY_WORK_TABS,
  MY_WORK_STATUS_FILTERS,
} from '@/features/my-work/logic';

import {
  getEntityId,
  getTaskStatus,
  getTaskPriority,
  getTaskDueDate,
  getTaskType,
  getTaskTypeInfo,
  getStatusMapping,
  getPriorityColor,
  getPriorityIconPath,
  getDueDateUrgency,
  getDisplayName,
  getTaskAssignee,
  formatDate,
  taskStatusMap,
  taskPriorityMap,
} from '@/features/task/logic';

import { FocusBar } from '@/components/FocusBar';
import { EntityAvatar } from '@/components/EntityAvatar';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Page size for ListMyTasks — "Load more" appends another page on top of
 *  this rather than fetching everything at once (see task 1 / framework-smqh.1). */
const MY_WORK_PAGE_SIZE = 20;

/** Delay before a search keystroke turns into a request (see task 1). */
const SEARCH_DEBOUNCE_MS = 300;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const MyWorkPage = observer(() => {
  const store = useTask();
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // -- State ----------------------------------------------------------------

  const [activeTab, setActiveTab] = useState<MyWorkTab>(
    (searchParams.get('tab') as MyWorkTab) || 'assigned',
  );
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<UrgencyGroup>>(new Set());
  const [activeFocusSegment, setActiveFocusSegment] = useState<FocusSegment | null>(null);

  // Pagination — ListMyTasks (the store method) replaces store.items on every
  // call rather than appending, so "Load more" has to accumulate locally.
  const [pageCursor, setPageCursor] = useState('');
  const [accumulatedTasks, setAccumulatedTasks] = useState<Task[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const querySignatureRef = useRef<string | null>(null);

  // -- Debounce search --------------------------------------------------------

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [search]);

  // Keep the URL shareable/bookmarkable without spamming history on every keystroke.
  useEffect(() => {
    const params: Record<string, string> = { tab: activeTab };
    if (statusFilter) params.status = statusFilter;
    if (debouncedSearch) params.search = debouncedSearch;
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, statusFilter, debouncedSearch]);

  // -- Data loading -----------------------------------------------------------
  //
  // ListMyTasks is a GET endpoint; the API client only flattens query params
  // one level deep (core/api/client.ts buildUrl), so the two-level-deep
  // `filters.statuses.statuses[]` may not reach the server even though it is
  // built correctly here (known framework defect framework-giph). We degrade
  // by re-applying status + search client-side below regardless of what the
  // backend actually honored.

  const querySignature = `${activeTab}:${statusFilter}:${debouncedSearch}`;

  useEffect(() => {
    const signatureChanged = querySignatureRef.current !== querySignature;
    querySignatureRef.current = querySignature;
    const cursorForThisRequest = signatureChanged ? '' : pageCursor;

    const filters: Record<string, unknown> = {};
    if (statusFilter) filters.statuses = { statuses: [statusFilter] };
    if (debouncedSearch) filters.search_query = debouncedSearch;

    const params: Record<string, unknown> = {
      pagination: {
        cursor: cursorForThisRequest,
        limit: MY_WORK_PAGE_SIZE,
        direction: 'forward',
      },
      filters,
      ...getMyWorkTabFilter(activeTab),
    };

    let cancelled = false;
    if (cursorForThisRequest) setIsLoadingMore(true);

    store.ListMyTasks(params as any).then(() => {
      if (cancelled) return;
      setAccumulatedTasks(prev =>
        cursorForThisRequest === '' ? store.items : [...prev, ...store.items],
      );
      setIsLoadingMore(false);
    }).catch(() => {
      setIsLoadingMore(false);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [querySignature, pageCursor, activeTab, statusFilter, debouncedSearch, store]);

  const handleLoadMore = useCallback(() => {
    const next = store.pageInfo?.next_cursor;
    if (next) setPageCursor(next);
  }, [store]);

  // -- Computed values ------------------------------------------------------

  // Client-side degrade layer for status/search (see comment above), applied
  // on top of whatever the server already filtered.
  const degradedTasks = useMemo(
    () => filterMyWorkTasks(accumulatedTasks, statusFilter, debouncedSearch),
    [accumulatedTasks, statusFilter, debouncedSearch],
  );

  const focusStats = useMemo(
    () => computeFocusStats(degradedTasks),
    [degradedTasks],
  );

  const visibleTasks = useMemo(
    () => filterTasksByFocusSegment(degradedTasks, activeFocusSegment),
    [degradedTasks, activeFocusSegment],
  );

  const urgencyGroups = useMemo(
    () => groupTasksByUrgency(visibleTasks),
    [visibleTasks],
  );

  const nonEmptyGroups = useMemo(
    () => URGENCY_GROUPS.filter(g => urgencyGroups[g.id].length > 0),
    [urgencyGroups],
  );

  const hasMore = store.pageInfo?.has_next ?? false;

  // -- Handlers -------------------------------------------------------------

  const handleTabChange = useCallback((tab: MyWorkTab) => {
    setActiveTab(tab);
    setActiveFocusSegment(null);
  }, []);

  const handleStatusFilterChange = useCallback((value: string) => {
    setStatusFilter(value);
  }, []);

  const toggleGroup = useCallback((groupId: UrgencyGroup) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  }, []);

  const handleTaskClick = useCallback((id: string) => {
    navigate(`/tasks/${id}`);
  }, [navigate]);

  const handleFocusBarClick = useCallback((filter: string) => {
    const segment = filter as FocusSegment;
    setActiveFocusSegment(prev => (prev === segment ? null : segment));
  }, []);

  // -- Render ---------------------------------------------------------------

  return (
    <div className="space-y-6">
      {/* Page header — the tab already names this page, so the heading is for screen readers only. */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="sr-only">{t('myWork.title')}</h1>
        <Link to="/tasks/new" className="sm:ml-auto">
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            {t('task.create')}
          </button>
        </Link>
      </div>

      {/* Focus bar */}
      <FocusBar
        overdueCount={focusStats.overdueCount}
        dueThisWeekCount={focusStats.dueThisWeekCount}
        awaitingReviewCount={focusStats.awaitingReviewCount}
        inProgressCount={focusStats.inProgressCount}
        onItemClick={handleFocusBarClick}
        activeFilter={activeFocusSegment}
      />

      {/* Tab navigation */}
      <div className="border-b border-border" role="tablist" aria-label={t('myWork.tabs.label')}>
        <div className="flex gap-0 -mb-px">
          {MY_WORK_TABS.map(tab => (
            <button
              key={tab.id}
              role="tab"
              type="button"
              aria-selected={activeTab === tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              {t(tab.labelKey)}
            </button>
          ))}
        </div>
      </div>

      {/* Status filter pills + search */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-2" role="group" aria-label={t('task.field.status')}>
          {MY_WORK_STATUS_FILTERS.map(pill => (
            <button
              key={pill.value || 'all'}
              type="button"
              onClick={() => handleStatusFilterChange(pill.value)}
              aria-pressed={statusFilter === pill.value}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                statusFilter === pill.value
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              {t(pill.labelKey)}
            </button>
          ))}
        </div>
        <Input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={t('common.search')}
          fullWidth={false}
          className="sm:w-64"
          aria-label={t('common.search')}
          leftIcon={(
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
              <circle cx="11" cy="11" r="8" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35" />
            </svg>
          )}
        />
      </div>

      {/* Active FocusBar filter banner */}
      {activeFocusSegment && (
        <div className="flex items-center justify-between rounded-md border border-border bg-muted/40 px-4 py-2 text-sm">
          <span className="text-foreground">
            {t('myWork.focusFilter.active', { segment: t(`myWork.focusBar.${activeFocusSegment === 'in_review' ? 'awaitingReview' : activeFocusSegment === 'due_this_week' ? 'dueThisWeek' : activeFocusSegment}`) })}
          </span>
          <Button variant="ghost" size="sm" onClick={() => setActiveFocusSegment(null)}>
            {t('myWork.focusFilter.clear')}
          </Button>
        </div>
      )}

      {/* Error state */}
      {store.error ? (
        <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />
      ) : null}

      {/* Loading state (initial load only — "load more" uses its own inline spinner) */}
      {store.isLoading && !isLoadingMore && accumulatedTasks.length === 0 && (
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <div className="space-y-3">
                  <div className="h-5 w-32 bg-muted rounded animate-pulse" />
                  {Array.from({ length: 2 }).map((_, j) => (
                    <div key={j} className="flex items-center gap-3">
                      <div className="h-4 w-4 bg-muted rounded animate-pulse" />
                      <div className="h-4 w-48 bg-muted rounded animate-pulse" />
                      <div className="h-5 w-20 bg-muted rounded-full animate-pulse" />
                      <div className="h-4 w-16 bg-muted rounded animate-pulse" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!store.isLoading && visibleTasks.length === 0 && !store.error && (
        <Card>
          <CardContent className="py-16 text-center">
            <svg
              className="mx-auto h-12 w-12 text-muted-foreground/50"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
              />
            </svg>
            <h3 className="mt-4 text-lg font-medium text-foreground">
              {t('myWork.empty.title')}
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              {t(`myWork.empty.${activeTab}`)}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Task groups by urgency */}
      {!store.isLoading && nonEmptyGroups.length > 0 && (
        <div className="space-y-4">
          {nonEmptyGroups.map(groupConfig => {
            const groupTasks = urgencyGroups[groupConfig.id];
            const isCollapsed = collapsedGroups.has(groupConfig.id);

            return (
              <div
                key={groupConfig.id}
                id={`urgency-group-${groupConfig.id}`}
                className="rounded-lg border border-border overflow-hidden"
              >
                {/* Group header */}
                <button
                  type="button"
                  onClick={() => toggleGroup(groupConfig.id)}
                  aria-expanded={!isCollapsed}
                  aria-controls={`urgency-group-content-${groupConfig.id}`}
                  className="flex w-full items-center gap-3 px-4 py-3 bg-card text-left transition-colors hover:bg-muted/50"
                >
                  <svg
                    className={`h-4 w-4 text-muted-foreground transition-transform flex-shrink-0 ${isCollapsed ? '' : 'rotate-90'}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                  <span className="text-sm font-semibold text-foreground">
                    {t(groupConfig.labelKey)}
                  </span>
                  <Badge
                    variant={groupConfig.variant === 'error' ? 'error' : groupConfig.variant === 'warning' ? 'warning' : groupConfig.variant === 'primary' ? 'primary' : 'secondary'}
                    size="sm"
                  >
                    {groupTasks.length}
                  </Badge>
                </button>

                {/* Group content */}
                {!isCollapsed && (
                  <div id={`urgency-group-content-${groupConfig.id}`}>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-8" />
                            <TableHead className="w-20">Key</TableHead>
                            <TableHead>{t('task.field.title')}</TableHead>
                            <TableHead className="w-24">{t('task.field.status')}</TableHead>
                            <TableHead className="w-10">{t('task.field.priority')}</TableHead>
                            <TableHead className="w-24">{t('task.field.dueDate')}</TableHead>
                            <TableHead className="w-28">{t('project.title')}</TableHead>
                            <TableHead className="w-10">{t('task.field.assignee')}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {groupTasks.map(task => (
                            <TaskRow
                              key={getEntityId(task)}
                              task={task}
                              onClick={handleTaskClick}
                              locale={locale}
                              t={t}
                            />
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination — accumulates locally since ListMyTasks replaces store.items per call */}
      {!activeFocusSegment && hasMore && !store.isLoading && (
        <div className="flex justify-center">
          <Button variant="secondary" onClick={handleLoadMore} isLoading={isLoadingMore}>
            {t('common.loadMore')}
          </Button>
        </div>
      )}
    </div>
  );
});

MyWorkPage.displayName = 'MyWorkPage';
export default MyWorkPage;

// ---------------------------------------------------------------------------
// TaskRow — compact row for urgency groups
// ---------------------------------------------------------------------------

interface TaskRowProps {
  task: Task;
  onClick: (id: string) => void;
  locale: string;
  t: (key: string) => string;
}

function TaskRow({ task, onClick, locale, t }: TaskRowProps) {
  const id = getEntityId(task);
  const status = getTaskStatus(task);
  const priority = getTaskPriority(task);
  const dueDate = getTaskDueDate(task);
  const taskType = getTaskType(task);
  const typeInfo = getTaskTypeInfo(taskType);
  const statusInfo = getStatusMapping(taskStatusMap, status);
  const priorityColor = getPriorityColor(priority);
  const priorityIconPath = getPriorityIconPath(priority);
  const dueUrgency = getDueDateUrgency(dueDate, status);
  const assignee = getTaskAssignee(task);
  const assigneeName = assignee ? getDisplayName(assignee) : null;

  return (
    <TableRow
      className="h-10 cursor-pointer transition-colors hover:bg-muted/50"
      onClick={() => onClick(id)}
    >
      {/* Type icon */}
      <TableCell>
        {typeInfo ? (
          <svg
            className="h-4 w-4 text-muted-foreground"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
            aria-label={t(typeInfo.labelKey)}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d={typeInfo.iconPath} />
          </svg>
        ) : (
          <span className="h-4 w-4 inline-block" />
        )}
      </TableCell>

      {/* Key */}
      <TableCell>
        <span className="text-xs text-muted-foreground font-mono">
          {id.slice(0, 8)}
        </span>
      </TableCell>

      {/* Title */}
      <TableCell>
        <span className="text-sm font-medium text-foreground truncate block">
          {(task as any).title || t('common.untitled')}
        </span>
      </TableCell>

      {/* Status badge */}
      <TableCell>
        <Badge variant={statusInfo.variant as any} size="sm">
          {t(statusInfo.labelKey)}
        </Badge>
      </TableCell>

      {/* Priority */}
      <TableCell>
        {priorityIconPath ? (
          <svg
            className={`h-4 w-4 ${priorityColor}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            aria-label={priority ? t(getStatusMapping(taskPriorityMap, priority).labelKey) : undefined}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d={priorityIconPath} />
          </svg>
        ) : (
          <span className="h-2.5 w-2.5 rounded-full bg-muted-foreground/30 inline-block" />
        )}
      </TableCell>

      {/* Due date */}
      <TableCell>
        {dueDate ? (
          <span className={`text-xs whitespace-nowrap ${
            dueUrgency === 'overdue' ? 'text-destructive font-medium' :
            dueUrgency === 'soon' ? 'text-yellow-600 dark:text-yellow-400' :
            'text-muted-foreground'
          }`}>
            {dueUrgency === 'overdue' && (
              <svg
                className="h-3 w-3 inline mr-0.5 -mt-px"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            )}
            {formatDate(dueDate, locale)}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">--</span>
        )}
      </TableCell>

      {/* Project */}
      <TableCell>
        <span className="text-xs text-muted-foreground truncate block">
          {(task as any).entity?.product_id || (task as any).project?.name || '--'}
        </span>
      </TableCell>

      {/* Assignee avatar */}
      <TableCell>
        {assigneeName ? (
          <EntityAvatar name={assigneeName} image={assignee?.avatar} userId={assignee?.entity?.id} size="xs" />
        ) : (
          <div
            className="h-6 w-6 rounded-full border-2 border-dashed border-border flex items-center justify-center flex-shrink-0"
            title={t('task.unassigned')}
          >
            <svg className="h-3 w-3 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
        )}
      </TableCell>
    </TableRow>
  );
}
